import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  InputBase,
  Tooltip,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { CloseIcon, ContentCopyIcon, DownloadIcon, CheckCircleIcon } from 'icons/materialIcons';
import { useCardContext } from 'contexts/CardContext';
import getIconForType from 'utils/icons/getIconForType';
import { useAuth } from 'contexts/AuthContext';
import QRCodeStyling from 'qr-code-styling';
import eventTrackingService from 'services/eventTrackingService';

const ShareYourCardModal = ({ open, onClose, profileUrl, cardId: specificCardId }) => {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [qrError, setQrError] = useState(false);
  const { cardState } = useCardContext();
  const { authState } = useAuth();
  const qrContainerRef = useRef(null);
  const qrRef = useRef(null);

  // Reset function to clear all state when modal closes
  const resetModalState = () => {
    setCopied(false);
    setShareUrl('');
    setLoading(true);
    setQrError(false);

    // Clear QR code container and reference
    if (qrContainerRef.current) {
      qrContainerRef.current.innerHTML = '';
    }
    qrRef.current = null;
  };

  // Handle modal close with proper cleanup
  const handleModalClose = () => {
    resetModalState();
    onClose();
  };

  // Reset modal state when modal opens
  useEffect(() => {
    if (open) {
      resetModalState();
    }
  }, [open]);

  // Get card details for QR code download filename
  const cardId = specificCardId || (profileUrl ? null : Object.keys(cardState)[0] || '');
  const cardName = specificCardId ? (cardState[specificCardId]?.cardName || 'My Card') :
    (authState?.user?.fullName || 'My Card');

  // Generate share URL
  useEffect(() => {
    if (!open) return;

    const baseUrl = window.location.origin;
    let shareLink;

    if (profileUrl) {
      shareLink = `${profileUrl}${profileUrl.includes('?') ? '&' : '?'}source=share_link`;
    } else if (specificCardId) {
      shareLink = `${baseUrl}/card/${specificCardId}?source=share_link`;
    } else {
      const userId = authState?.user?.userId;
      const profileSlug = authState?.user?.profileSlug;
      const pathSegment = profileSlug || userId;
      shareLink = pathSegment ? `${baseUrl}/profile/${pathSegment}?source=share_link` : '';
    }

    setShareUrl(shareLink);
    setCopied(false);
    setQrError(false);
  }, [open, profileUrl, specificCardId, authState?.user]);

  // Generate QR code
  useEffect(() => {
    if (!open || !shareUrl || !qrContainerRef.current) return;

    const generateQR = async () => {
      try {
        setLoading(true);
        setQrError(false);

        // Clear previous content and reset state
        if (qrContainerRef.current) {
          qrContainerRef.current.innerHTML = '';
        }

        // Reset QR reference
        qrRef.current = null;

        // Calculate the appropriate size for the QR code
        // Use a slightly smaller size than the container to ensure it fits
        const containerSize = 200; // Fixed size, smaller than the container

        // Create QR code instance
        qrRef.current = new QRCodeStyling({
          width: containerSize,
          height: containerSize,
          type: "png", // PNG for better scanning
          data: shareUrl,
          dotsOptions: {
            color: "#000000",
            type: "square"
          },
          cornersSquareOptions: {
            type: "square"
          },
          cornersDotOptions: {
            type: "square"
          },
          backgroundOptions: {
            color: "#ffffff"
          },
          qrOptions: {
            errorCorrectionLevel: "Q" // Medium-high level for better scanning
          },
          margin: 2 // Very small margin to maximize QR code size
        });

        // Add slight delay to ensure DOM is ready
        setTimeout(async () => {
          try {
            if (qrContainerRef.current && qrRef.current) {
              await qrRef.current.append(qrContainerRef.current);

              // Add a small delay to ensure the QR code is fully rendered
              setTimeout(() => {
                setLoading(false);
              }, 300);
            }
          } catch (appendError) {
            setQrError(true);
            setLoading(false);
          }
        }, 300);
      } catch (error) {
        setQrError(true);
        setLoading(false);
      }
    };

    generateQR();

    // Cleanup function
    return () => {
      if (qrRef.current) {
        qrRef.current = null;
      }
      // Clear the container when component unmounts or dependencies change
      if (qrContainerRef.current) {
        qrContainerRef.current.innerHTML = '';
      }
    };
  }, [shareUrl, open]); // Keep these dependencies
  // Record share attempt for analytics purposes
  const recordShareAttempt = async (shareType) => {
    if (!cardId) return;

    try {
      // Use the event tracking service directly instead of API call
      // This ensures the same tracking mechanism is used for all events
      const tracking = eventTrackingService;
      tracking.trackShare({
        cardId,
        shareType,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);

      // Record the share attempt for analytics
      recordShareAttempt('copy_link');
    });
  };

  const handleDownloadQR = async () => {
    // Record the share attempt for analytics
    await recordShareAttempt('qr_download');

    if (qrRef.current) {
      try {
        await qrRef.current.download({
          extension: 'png',
          name: `${cardName.replace(/\s+/g, '-')}-QR-Code`
        });
      } catch (error) {
        // Fallback download method if the library method fails
        try {
          const canvas = document.createElement('canvas');
          const svgElement = qrContainerRef.current?.querySelector('svg');

          if (svgElement) {
            const ctx = canvas.getContext('2d');
            canvas.width = 1000;
            canvas.height = 1000;

            const svgData = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0, 1000, 1000);
              URL.revokeObjectURL(url);

              const link = document.createElement('a');
              link.download = `${cardName.replace(/\s+/g, '-')}-QR-Code.png`;
              link.href = canvas.toDataURL('image/png');
              link.click();
            };

            img.src = url;
          }
        } catch (fallbackError) {
          // Fallback download method failed
        }
      }
    }
  };

  // Handler for social shares
  const handleSocialShare = (shareType) => {
    // Record the share attempt for analytics
    recordShareAttempt(shareType);
  };

  // Define share options with type for icon lookup
  const shareOptions = [
    { type: 'Email', title: 'Email', url: `mailto:?subject=My Digital Business Card&body=Check out my digital business card: ${shareUrl}`, color: '#F44336' },
    { type: 'LinkedIn', title: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, color: '#0077B5' },
    { type: 'Twitter', title: 'Twitter/X', url: `https://twitter.com/intent/tweet?text=Check%20out%20my%20digital%20business%20card:%20${encodeURIComponent(shareUrl)}`, color: '#1DA1F2' },
    { type: 'WhatsApp', title: 'WhatsApp', url: `https://api.whatsapp.com/send?text=Check%20out%20my%20digital%20business%20card:%20${encodeURIComponent(shareUrl)}`, color: '#25D366' }
  ];

  const ShareOption = ({ type, title, url, color }) => (
    <Grid item xs={6} sm={6} md={6}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'none', width: '100%', display: 'block' }}
        onClick={() => handleSocialShare(type.toLowerCase())}
      >
        <Button
          variant="outlined"
          fullWidth
          sx={{
            backgroundColor: '#f5f5f5',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            borderColor: '#ddd',
            color: '#555',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            padding: '10px 16px',
            borderRadius: '8px',
            height: '48px',
            '&:hover': {
              backgroundColor: '#ececec',
              borderColor: '#ccc'
            },
          }}
          startIcon={getIconForType(type)}
        >
          {title}
        </Button>
      </a>
    </Grid>
  );

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="share-card-modal"
      disableRestoreFocus
      closeAfterTransition
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(245, 245, 245, 0.7)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '650px' },
          maxHeight: { xs: '95vh', sm: '85vh' },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          outline: 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Box
          sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleModalClose();
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            component="h2"
            align="center"
            fontWeight={600}
            sx={{ mb: 3 }}
          >
            Share Your Card
          </Typography>

          {/* QR Code Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 4 }}>
            <Box
              sx={{
                width: { xs: 200, sm: 220, md: 230 },
                height: { xs: 200, sm: 220, md: 230 },
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 3,
                bgcolor: '#fff',
                position: 'relative',
                padding: 0, // Remove padding to maximize space
                overflow: 'hidden', // Hide overflow
              }}
            >
              {loading && (
                <CircularProgress
                  size={40}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1
                  }}
                />
              )}

              {qrError && (
                <Typography color="error" align="center">
                  Could not load QR code. Please try again.
                </Typography>
              )}

              <Box
                ref={qrContainerRef}
                key={`qr-${open}-${shareUrl}`} // Force re-render when modal opens or URL changes
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '& img, & svg': {
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  },
                  '& canvas': {
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  },
                  '& div': {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%'
                  }
                }}
              />
            </Box>

            <Button
              variant="outlined"
              onClick={handleDownloadQR}
              startIcon={<DownloadIcon />}
              disabled={loading || qrError}
              sx={{
                borderRadius: 6,
                px: 3,
                py: 1.2,
                borderColor: '#ddd',
                color: '#555',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#bbb',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Download QR Code
            </Button>
          </Box>

          {/* Divider */}
          <Divider sx={{ width: '100%', my: 2 }} />

          {/* Copy Link Section */}
          <Box sx={{ width: '100%', mb: 4 }}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Copy Card Link
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: '#f7f7f7',
                  flexGrow: 1,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1, fontSize: '0.9rem', color: '#666' }}
                  value={shareUrl}
                  readOnly
                />
              </Paper>

              <Tooltip
                title={copied ? "Copied!" : "Copy to clipboard"}
                placement="top"
                arrow
              >
                <Button
                  onClick={handleCopyLink}
                  variant="contained"
                  disableElevation
                  startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    px: 3,
                    py: 1.5,
                    bgcolor: copied ? '#4caf50' : '#29AEF8',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: copied ? '#43a047' : '#1e88e5'
                    },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  {copied ? 'Copied' : 'Copy Link'}
                </Button>
              </Tooltip>
            </Box>
          </Box>

          {/* Divider */}
          <Divider sx={{ width: '100%', my: 2 }} />

          {/* Share Card Section */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" fontWeight={600} mb={2}>
              Share Card
            </Typography>

            <Grid container spacing={2}>
              {/* Map through share options array */}
              {shareOptions.map((option) => (
                <ShareOption
                  key={option.type}
                  type={option.type}
                  title={option.title}
                  url={option.url}
                  color={option.color}
                />
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ShareYourCardModal;